import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { TokenStorageService } from 'src/app/core/services/auth/tokens/token-storage.service';
import { NotificationService } from 'src/app/core/services/notifications/notifications.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-header-debug-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './header-debug-panel.component.html',
  styleUrl: './header-debug-panel.component.scss',
})
export class HeaderDebugPanelComponent {
  private readonly authService = inject(AuthService);
  private readonly tokenStorageService = inject(TokenStorageService);
  private readonly notificationService = inject(NotificationService);

  readonly showDebugPanel = !environment.production;
  readonly panelOpen = signal(false);

  togglePanel(): void {
    this.panelOpen.update((v) => !v);
  }

  deleteToken(): void {
    this.tokenStorageService.removeToken();
    this.notificationService.showSnackBar({
      message: 'Access token deleted from storage.',
      type: 'info',
    });
    this.panelOpen.set(false);
  }

  invalidateRefreshToken(): void {
    this.authService
      .invalidateRefreshToken()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.showSnackBar({
            message: 'Refresh token invalidated on server.',
            type: 'success',
          });
        },
        error: (err: Error) => {
          this.notificationService.showSnackBar({
            message: `Failed to invalidate refresh token: ${err?.message ?? 'Unknown error'}`,
            type: 'error',
          });
        },
      });
    this.panelOpen.set(false);
  }

  testRefresh(): void {
    this.authService
      .refreshToken()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.showSnackBar({
            message: 'Refresh succeeded — new access token received.',
            type: 'success',
          });
        },
        error: (err: Error) => {
          this.notificationService.showSnackBar({
            message: `Refresh failed: ${err?.message ?? 'Unknown error'}`,
            type: 'error',
          });
        },
      });
    this.panelOpen.set(false);
  }

  deactivateAccount(): void {
    const confirmed = confirm('Are you sure you want to deactivate this account? This action cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.panelOpen.set(false);
    this.authService
      .deactivateCurrentUser()
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.showSnackBar({
            message: 'Account deactivated successfully.',
            type: 'success',
          });
        },
        error: (err: Error) => {
          this.notificationService.showSnackBar({
            message: `Deactivation failed: ${err?.message ?? 'Unknown error'}`,
            type: 'error',
          });
        },
      });
  }
}
